<script setup lang="ts">
import { ref, computed } from 'vue'
import { RouterLink } from 'vue-router'
import {
  ChevronRight, Smartphone, Car, Gem, Palette,
  Building2, Tractor, Wheat, Flame, ArrowRight,
  Layers, Sparkles, Gavel, Sofa
} from 'lucide-vue-next'
import { useI18n } from '@/composables/useI18n'
import CategoryArt from '@/components/category/CategoryArt.vue'
import SubcategoryArt from '@/components/category/SubcategoryArt.vue'
import { useThemeStore } from '@/stores/theme'
import { platformCategories } from '@/data/categories'

const { t, currentLocale } = useI18n()
// Admin > Design > Categories: 'shyrdak' | 'object' (our vector art) or 'photo'.
const artStyle = computed(() => useThemeStore().theme.categoryArtStyle || 'shyrdak')

const emit = defineEmits<{
  navigate: []
}>()

const iconMap: Record<string, any> = {
  electronics: Smartphone,
  vehicles: Car,
  jewelry: Gem,
  art: Palette,
  'real-estate': Building2,
  machinery: Tractor,
  livestock: Wheat,
  'home-goods': Sofa,
}

const localizedCategories = computed(() => {
  const lang = (currentLocale.value?.code as 'ky' | 'ru' | 'tr') || 'ru'
  return platformCategories.map(cat => {
    let tags: string[] = []
    if (cat.popularTags && typeof cat.popularTags === 'object') {
      if (Array.isArray(cat.popularTags)) {
        tags = cat.popularTags
      } else if (lang in cat.popularTags) {
        tags = (cat.popularTags as any)[lang] || []
      }
    }

    return {
      id: cat.id,
      slug: cat.slug,
      name: cat.name[lang] || cat.name.ky,
      description: cat.description[lang] || cat.description.ky,
      icon: iconMap[cat.slug] || Layers,
      coverImage: cat.coverImage,
      count: cat.count,
      popularTags: tags,
      subCategories: cat.subCategories.map(sub => ({
        id: sub.id,
        slug: sub.slug,
        name: sub.name[lang] || sub.name.ky
      }))
    }
  })
})

const activeIndex = ref(0)
const activeCategory = computed(() => localizedCategories.value[activeIndex.value] || localizedCategories.value[0])

function setActive(index: number) {
  activeIndex.value = index
}
</script>

<template>
  <div class="flex w-[880px] max-w-[calc(100vw-2rem)] flex-col bg-surface text-text-primary">
    <!-- Two-panel body -->
    <div class="flex min-h-[420px]">

      <!-- Left rail: the seven markets -->
      <div class="w-[300px] shrink-0 border-r border-border bg-background px-2 py-2.5">
        <p class="px-3 pb-1.5 pt-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-text-muted">
          {{ t('nav.allCategories') }}
        </p>
        <div class="space-y-0.5">
          <RouterLink
            v-for="(cat, index) in localizedCategories"
            :key="cat.slug"
            :to="`/auctions?category=${cat.slug}`"
            role="menuitem"
            class="group flex w-full items-center justify-between gap-2 rounded-xl px-2.5 py-2 text-left text-[13px] font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            :class="index === activeIndex
              ? 'bg-accent text-text-primary'
              : 'text-text-secondary hover:bg-accent/60 hover:text-text-primary'"
            @mouseenter="setActive(index)"
            @focus="setActive(index)"
            @click="emit('navigate')"
          >
            <span class="flex min-w-0 items-center gap-2.5">
              <span
                class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition"
                :class="index === activeIndex ? 'bg-primary text-onPrimary' : 'bg-primary/20 text-onPrimaryContainer'"
              >
                <component :is="cat.icon" class="h-4 w-4" />
              </span>
              <span class="truncate leading-tight">{{ cat.name }}</span>
            </span>
            <ChevronRight
              class="h-3.5 w-3.5 shrink-0 transition-transform"
              :class="index === activeIndex ? 'translate-x-0.5 text-text-primary' : 'text-text-muted opacity-60'"
            />
          </RouterLink>
        </div>
      </div>

      <!-- Right panel: active category detail -->
      <div v-if="activeCategory" class="flex min-w-0 flex-1 flex-col p-5">
        <div class="flex items-start justify-between gap-3 border-b border-border pb-4">
          <div class="min-w-0">
            <h3 class="font-display text-[15px] font-semibold text-text-primary">
              {{ activeCategory.name }}
            </h3>
            <p class="mt-1 line-clamp-1 text-xs text-text-secondary">
              {{ activeCategory.description }}
            </p>
          </div>
          <!-- Real per-category volume from src/data/categories.ts — never a
               fabricated "live" status: red is reserved for genuine live state. -->
          <span class="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-primary/20 px-2.5 py-1 text-[11px] font-semibold text-onPrimaryContainer">
            <Gavel class="h-3 w-3 shrink-0" />
            <span><span class="font-bold tabular">{{ activeCategory.count }}</span> {{ t('megaMenu.activeLots') }}</span>
          </span>
        </div>

        <!-- Sub-categories -->
        <p class="mb-2 mt-4 text-[11px] font-semibold uppercase tracking-[0.14em] text-text-muted">
          {{ t('categoriesPage.subcategories') }}
        </p>
        <div class="grid grid-cols-2 gap-2">
          <RouterLink
            v-for="sub in activeCategory.subCategories"
            :key="sub.slug"
            :to="`/auctions?category=${activeCategory.slug}&subCategory=${sub.slug}`"
            role="menuitem"
            class="group flex items-center justify-between gap-2 rounded-xl border border-border bg-surface py-2 pl-2 pr-3 text-xs font-semibold text-text-primary transition hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            @click="emit('navigate')"
          >
            <SubcategoryArt :slug="sub.slug" class="h-8 w-8" />
            <span class="min-w-0 flex-1 truncate">{{ sub.name }}</span>
            <ChevronRight class="h-3.5 w-3.5 shrink-0 text-text-muted transition group-hover:translate-x-0.5 group-hover:text-text-primary" />
          </RouterLink>
        </div>

        <!-- Popular tags -->
        <div v-if="activeCategory.popularTags?.length" class="mt-4 border-t border-border pt-3">
          <div class="mb-2 inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-text-muted">
            <Flame class="h-3.5 w-3.5 text-primary-hover" />
            <span>{{ t('megaMenu.popularSearches') || 'Популярдуу издөөлөр' }}</span>
          </div>
          <div class="flex flex-wrap gap-1.5">
            <RouterLink
              v-for="tag in activeCategory.popularTags"
              :key="tag"
              :to="`/auctions?category=${activeCategory.slug}&search=${encodeURIComponent(tag)}`"
              role="menuitem"
              class="rounded-full bg-accent px-2.5 py-1 text-[11px] font-semibold text-text-secondary transition hover:bg-primary/20 hover:text-onPrimaryContainer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              @click="emit('navigate')"
            >
              {{ tag }}
            </RouterLink>
          </div>
        </div>

        <!-- View-all banner -->
        <div class="mt-auto pt-4">
          <RouterLink
            :to="`/auctions?category=${activeCategory.slug}`"
            role="menuitem"
            class="group relative flex h-16 items-center justify-between gap-3 overflow-hidden rounded-xl bg-ink px-4 text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            @click="emit('navigate')"
          >
            <template v-if="artStyle === 'photo'">
              <img
                :src="activeCategory.coverImage"
                :alt="activeCategory.name"
                class="absolute inset-0 h-full w-full object-cover opacity-60 transition-transform duration-500 group-hover:scale-105"
                loading="lazy"
              />
              <div class="absolute inset-0 bg-gradient-to-r from-ink/90 via-ink/60 to-ink/30" />
            </template>
            <span v-else class="relative h-11 w-11 shrink-0 overflow-hidden rounded-lg">
              <CategoryArt :slug="activeCategory.slug" :variant="artStyle" />
            </span>
            <div class="relative min-w-0">
              <p class="truncate text-xs font-bold text-primary">{{ activeCategory.name }}</p>
              <p class="truncate text-[11px] text-white/70">{{ t('megaMenu.viewAllIn', { category: activeCategory.name }) }}</p>
            </div>
            <span class="relative inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-onPrimary transition-transform group-hover:translate-x-0.5">
              <ArrowRight class="h-4 w-4" />
            </span>
          </RouterLink>
        </div>
      </div>
    </div>

    <!-- Footer bar -->
    <div class="flex items-center justify-between gap-3 border-t border-border bg-background px-5 py-2.5 text-xs">
      <span class="inline-flex min-w-0 items-center gap-1.5 text-text-muted">
        <Sparkles class="h-3.5 w-3.5 shrink-0 text-primary-hover" />
        <span class="truncate">{{ t('megaMenu.allSectors') || '7 негизги тармак, 30+ чакан категория' }}</span>
      </span>
      <RouterLink
        to="/categories"
        role="menuitem"
        class="group inline-flex shrink-0 items-center gap-1 font-semibold text-text-primary transition hover:text-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        @click="emit('navigate')"
      >
        <span>{{ t('megaMenu.browseAll') || 'Бардык категорияларды көрүү' }}</span>
        <ArrowRight class="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
      </RouterLink>
    </div>
  </div>
</template>
